import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useUser } from '../../context/UserContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding, faNetworkWired, faArrowTrendUp,
  faGear, faArrowRightFromBracket, faUser, faClipboardCheck,
  faDollarSign, faCalendarDays, faFeather, faPenNib,
  faEnvelope, faInbox, faLightbulb, faRepeat, faHammer, faServer
} from '@fortawesome/free-solid-svg-icons';
import useAvatar from '../../hooks/useAvatar';
import Avatar from '../ui/Avatar';

export default function BottomNavigation() {
  const { user, logout } = useUser();
  const location = useLocation();
  const avatarUrl = useAvatar();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  // Check user permissions
  const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();
  const userRole = normalizeRole(user?.role);
  const hasFullAccess = userRole === 'admin' || userRole === 'developer' || userRole === 'authenticated' || userRole === 'agency_owner';
  const isAdmin = userRole === 'admin';

  const handleLogout = () => {
    setIsMoreMenuOpen(false);
    logout();
  };

  const isOn = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Primary navigation items (always visible) — Kyle's pick, in his order
  const primaryNavItems = [
    ...(hasFullAccess ? [
      {
        to: '/fulfillment',
        icon: faClipboardCheck,
        // "Fulfillment" truncates at 375px in the mono face
        label: 'HQ',
        isActive: isOn('/fulfillment')
      }
    ] : []),
    ...(isAdmin ? [
      {
        to: '/sales',
        icon: faArrowTrendUp,
        label: 'Sales',
        isActive: isOn('/sales'),
      }
    ] : []),
    ...(hasFullAccess ? [
      {
        to: '/client-comms',
        icon: faInbox,
        label: 'Comms',
        isActive: isOn('/client-comms')
      },
      {
        to: '/clients',
        icon: faBuilding,
        label: 'Clients',
        isActive: isOn('/clients')
      }
    ] : []),
    // Users without full access have nothing above, so Network stays up front for them
    ...(!hasFullAccess ? [
      {
        to: '/private-network',
        icon: faNetworkWired,
        label: 'Network',
        isActive: isOn('/private-network')
      }
    ] : []),
  ];

  // Secondary navigation items (in "more" menu)
  const secondaryNavItems = [
    ...(hasFullAccess ? [
      {
        to: '/inbox',
        icon: faEnvelope,
        label: 'Inbox',
        isActive: isOn('/inbox')
      }
    ] : []),
    ...(hasFullAccess ? [
      {
        to: '/ideation',
        icon: faLightbulb,
        label: 'Ideation',
        isActive: location.pathname === '/ideation'
      }
    ] : []),
    ...(hasFullAccess ? [
      {
        to: '/builds',
        icon: faHammer,
        label: 'Builds',
        isActive: location.pathname === '/builds'
      }
    ] : []),
    ...(hasFullAccess ? [
      {
        to: '/recurring',
        icon: faRepeat,
        label: 'Recurring',
        isActive: location.pathname === '/recurring'
      }
    ] : []),
    ...(isAdmin ? [
      {
        to: '/agents',
        icon: faFeather,
        label: 'Agents',
        isActive: location.pathname === '/agents' || location.pathname.startsWith('/agents/')
      }
    ] : []),
    ...(hasFullAccess ? [
      {
        to: '/content',
        icon: faPenNib,
        label: 'Content',
        isActive: location.pathname === '/content' || location.pathname.startsWith('/content/')
      }
    ] : []),
    ...(hasFullAccess ? [
      {
        to: '/hosting',
        icon: faServer,
        label: 'Hosting',
        // Uptime, Private Network and the Security views live under Hosting's submenu.
        isActive: ['/hosting', '/uptime', '/security', '/private-network'].some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`))
      }
    ] : []),
    ...(isAdmin ? [
      {
        to: '/financials',
        icon: faDollarSign,
        label: 'Financials',
        isActive: location.pathname === '/financials'
      }
    ] : []),
    ...(hasFullAccess ? [
      {
        to: '/bookings',
        icon: faCalendarDays,
        label: 'Bookings',
        isActive: location.pathname === '/bookings' || location.pathname.startsWith('/bookings/')
      }
    ] : []),
    {
      to: '/settings',
      icon: faGear,
      label: 'Settings',
      isActive: location.pathname.startsWith('/settings')
    }
  ];

  const itemStyle = (isActive: boolean): React.CSSProperties => ({
    color: isActive ? 'var(--accent-text)' : 'var(--text-muted)',
    background: isActive ? 'var(--accent-soft)' : 'transparent',
  });

  return (
    <>
      {/* Bottom Navigation Bar - Mobile Only */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden"
        style={{
          background: 'var(--surface-sidebar)',
          borderTop: '1px solid var(--border-hairline)',
          paddingBottom: 'var(--sab)',
          paddingLeft: 'var(--sal)',
          paddingRight: 'var(--sar)',
        }}
      >
        <div className="px-2 py-1.5">
          <div className="flex items-center justify-around">
            {/* Primary Nav Items */}
            {primaryNavItems.slice(0, 4).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setIsMoreMenuOpen(false)}
                className="flex-1 min-w-0 flex flex-col items-center justify-center min-h-[48px] px-1 py-1.5 rounded-lg pressable"
                style={itemStyle(item.isActive)}
              >
                <FontAwesomeIcon icon={item.icon} className="text-base mb-1" />
                <span className="text-xs font-medium truncate max-w-full">{item.label}</span>
              </Link>
            ))}

            {/* More Menu Button */}
            <button
              onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
              className="flex-1 min-w-0 flex flex-col items-center justify-center min-h-[48px] px-1 py-1.5 rounded-lg pressable"
              style={itemStyle(isMoreMenuOpen || secondaryNavItems.some(item => item.isActive))}
            >
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="avatar"
                  className="w-5 h-5 rounded-full mb-1 object-cover"
                />
              ) : (
                <FontAwesomeIcon icon={faUser} className="text-base mb-1" />
              )}
              <span className="text-xs font-medium">More</span>
            </button>
          </div>
        </div>
      </div>

      {/* More Menu Modal */}
      <AnimatePresence>
        {isMoreMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMoreMenuOpen(false)}
              className="fixed inset-0 z-50 md:hidden"
              style={{ background: 'var(--surface-overlay)' }}
            />

            {/* Menu Panel */}
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed left-3 right-3 z-50 md:hidden flex flex-col"
              style={{
                // sits just above the nav bar; scrolls if the list outgrows a small phone
                bottom: 'calc(var(--bottom-nav-h) + var(--sab) + 8px)',
                maxHeight: 'calc(100dvh - var(--sat) - var(--bottom-nav-h) - var(--sab) - 20px)',
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-pop)',
              }}
            >
              <div className="p-3 overflow-y-auto overscroll-contain">
                {/* User Profile Section */}
                {user && (
                  <div className="mb-3 pb-3 px-1" style={{ borderBottom: '1px solid var(--border-hairline)' }}>
                    <div className="flex items-center">
                      <Avatar name={user.name} src={avatarUrl} size={40} />
                      <div className="ml-3 min-w-0">
                        <p className="font-medium text-sm" style={{ color: 'var(--text-body)' }}>
                          {user.name}
                        </p>
                        <p className="t-num text-xs truncate" style={{ color: 'var(--text-faint)' }}>
                          {user.email}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Secondary Navigation Items — two columns so the whole list fits a phone */}
                <div className="grid grid-cols-2 gap-1 mb-3">
                  {secondaryNavItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMoreMenuOpen(false)}
                      className="flex items-center min-h-[48px] px-3 rounded-lg hover-wash pressable"
                      style={itemStyle(item.isActive)}
                    >
                      <FontAwesomeIcon icon={item.icon} className="text-base w-5 text-center mr-3 shrink-0" />
                      <span className="text-sm font-medium truncate">{item.label}</span>
                    </Link>
                  ))}
                </div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center min-h-[48px] px-3 rounded-lg hover-wash pressable"
                  style={{ color: 'var(--danger-400)' }}
                >
                  <FontAwesomeIcon icon={faArrowRightFromBracket} className="text-base w-5 text-center mr-3" />
                  <span className="text-sm font-medium">Logout</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Bottom padding for content to avoid being hidden behind the nav */}
      <div className="md:hidden" style={{ height: 'calc(var(--bottom-nav-h) + var(--sab) + 16px)' }} />
    </>
  );
}
